// RHF uses a lot of underscore-dangle.
/* eslint-disable no-underscore-dangle */
import { noop } from 'lodash-es';
import { useEffect, useMemo } from 'react';
import type { Field } from 'react-hook-form';
import { useFormContext, get } from 'react-hook-form';

type NestedFieldElement = HTMLInputElement | HTMLSelectElement;

type FocusHandler = HTMLInputElement['focus'];
type SelectHandler = HTMLInputElement['select'];
type SetCustomValidityHandler = HTMLInputElement['setCustomValidity'];
type ReportValidityHandler = HTMLInputElement['reportValidity'];

interface NestedFieldRef {
    _f: {
        name: string;
        ref: {
            focus: FocusHandler;
            select: SelectHandler;
            setCustomValidity: SetCustomValidityHandler;
            reportValidity: ReportValidityHandler;
        };
    };
}

// A helper type for the internal field object from React Hook Form,
// extended with our custom `nestedRefs` property.
type RhfFieldWithNestedRefs = Field & { nestedRefs?: Record<string, NestedFieldRef> };

export class NestedFieldsRegistry {
    public fields: Record<string, NestedFieldRef>;

    constructor(public controllerName: string) {
        this.fields = {};
    }

    register(nestedName: string) {
        return {
            ref: (element: NestedFieldElement | null) => {
                if (element) {
                    // Structure below is copied from RHF source:
                    // https://github.com/react-hook-form/react-hook-form/blob/13465d93a1b4a173cbd9e98283341656a13f9cef/src/useController.ts#L155-L169
                    this.fields[nestedName] = {
                        _f: {
                            name: this.controllerName + nestedName,
                            ref: {
                                focus: element.focus ? element.focus.bind(element) : noop,
                                select:
                                    'select' in element && typeof element.select === 'function'
                                        ? element.select.bind(element)
                                        : noop,
                                setCustomValidity: element.setCustomValidity.bind(element),
                                reportValidity: element.reportValidity.bind(element),
                            },
                        },
                    };
                } else {
                    delete this.fields[nestedName];
                }
            },
        };
    }
}

/**
 * A hook to create and manage a registry for nested fields within a
 * single React Hook Form controller.
 */
export function useNestedFields(controllerName: string) {
    const { control } = useFormContext();

    const nestedFieldsRegistry = useMemo(
        () => new NestedFieldsRegistry(controllerName),
        [controllerName]
    );

    useEffect(() => {
        const field = get(control._fields, controllerName) as RhfFieldWithNestedRefs | undefined;

        if (field) {
            field.nestedRefs = nestedFieldsRegistry.fields;
        }
    }, [controllerName, control._fields, nestedFieldsRegistry.fields]);

    return nestedFieldsRegistry;
}
